import { FC, useState, useEffect } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { Popover } from '../Atoms/Popover'
import Dropdown from '../Atoms/Dropdown'
import {
  PrintShipmenttEnum,
  ShipmentContentInvoiceInterfaces,
  ShipmentInterface,
  ShipmentTracking,
  StoreShipmentInterface,
} from '../../interfaces'
import { FormCheckbox } from '../FormFields/FormCheckbox'
import { ShipmentDetailsModal } from './ShipmentDetailsModal'
import {
  PrinterIcon,
  ChevronDownIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import { ShipmentPrintModal } from '../ShipmentV2/ShipmentPrintModal'
import { ShipmentPiecesPrintModal } from '../ShipmentV2/ShipmentPrintPiecesModal'
import { PrintLabel } from '../../services/printerServices'
import { mapServiceToEnum } from '../../utils/mappers'
import { ShipmentService } from '../../interfaces/Services/ServicesEnum'
import { getShipmentContentInvoice, getShipmentTracking } from '../../services'
import { ShipmentDetailModal } from './ShipmentDetailModal'
import { shipmentStatusFormat } from '../../utils/format'
import { ShipmentStatus } from '../../modules/ShipmentV2/filterOptions'
import { ModalLoading } from '../Atoms/ModalLoading'
import { ConfirmModalStatus } from '../Preshipment/ModalSuccess'

export enum ShipmentField {
  SHIPMENT,
  PIECES,
  WEIGHT,
  VOLUME_CM3,
  VOLUME_CU3,
  ALIAS,
  PHONE,
  CITY,
  DATE,
  TRACKING,
  GUIDEALLIED,
}

const TableOptionsButton = (
  <div className="flex w-8 h-8 items-center justify-center">
    <EllipsisVerticalIcon className="w-5 h-5" />
  </div>
)

interface ShipmentTableProps {
  selectedAll: boolean
  shipments: StoreShipmentInterface[]
  selectedShipments: StoreShipmentInterface[]
  sortBy: { field: ShipmentField; asc: boolean }
  onChangeSortBy: (field: ShipmentField) => void
  onSelectShipment: (shipment: StoreShipmentInterface) => void
  onSelectAll: () => void
  onSuccessAction?: () => void
}
export const ShipmentTable: FC<ShipmentTableProps> = ({
  selectedAll,
  shipments,
  selectedShipments,
  sortBy,
  onChangeSortBy,
  onSelectShipment,
  onSelectAll,
  onSuccessAction,
}) => {
  const [openModal, setOpenModal] = useState(false)
  const [selected, setSelected] = useState<StoreShipmentInterface>()
  const [openPrintModal, setOpenPrintModal] = useState(false)
  const [openPrintHelpModal, setOpenPrintHelpModal] = useState(false)
  const [printHelpFormat, setPrintHelpFormat] = useState(1)
  const [openPrintPiecesModal, setOpenPrintPiecesModal] = useState(false)
  const [selectTypePiecesPrint, setSelectTypePiecesPrint] = useState(2)
  const [openDetailsModal, setOpenDetailsModal] = useState(false)
  const [typePrint, setTypePrint] = useState(0)
  const [openLoadingModal, setOpenLoadingModal] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState<ConfirmModalStatus>(
    ConfirmModalStatus.PROCESSING
  )
  const [loadingTitle, setLoadingTitle] = useState('Imprimiendo...')

  useEffect(() => {
    const handleZebraPrint = async () => {
      setOpenLoadingModal(true)
      setLoadingStatus(ConfirmModalStatus.PROCESSING)
      setLoadingTitle('Imprimiendo etiqueta Zebra...')

      try {
        const ok = await PrintLabel(
          parseInt(selected?.shipmentNumber ?? '0'),
          parseInt(selected?.serviceCode ?? '60'),
          selected?.buCodeSource ?? '',
          [],
          printHelpFormat
        )

        if (ok) {
          setLoadingStatus(ConfirmModalStatus.SUCCESS)
          setLoadingTitle(
            `Impresión completada\nGuía: ${selected?.shipmentNumber}`
          )
        } else {
          setLoadingStatus(ConfirmModalStatus.ERROR)
          setLoadingTitle(
            `Error al imprimir\nGuía: ${selected?.shipmentNumber}`
          )
        }
      } catch (error) {
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          `Error inesperado al imprimir\nGuía: ${selected?.shipmentNumber}`
        )
      } finally {
        setOpenPrintHelpModal(false)
      }
    }

    if (openPrintHelpModal && selected) {
      handleZebraPrint()
    }
  }, [openPrintHelpModal, selected])

  useEffect(() => {
    if (typePrint > 0 && selected) {
      switch (typePrint) {
        case PrintShipmenttEnum.DocumentoBolsillo:
          setOpenPrintModal(true)
          break
        case PrintShipmenttEnum.EtiquetaBomPequenna:
          setOpenPrintPiecesModal(true)
          setSelectTypePiecesPrint(1)
          break
        case PrintShipmenttEnum.EtiquetaBomGrande:
          setOpenPrintPiecesModal(true)
          setSelectTypePiecesPrint(2)
          break
        case PrintShipmenttEnum.EtiquetaBomAmbas:
          setOpenPrintPiecesModal(true)
          setSelectTypePiecesPrint(3)
          break
        case PrintShipmenttEnum.EtiquetaZebra:
          setOpenPrintHelpModal(true)
          setPrintHelpFormat(0)
          break
        case PrintShipmenttEnum.EtiquetaZebra10x10:
          setOpenPrintHelpModal(true)
          setPrintHelpFormat(1)
          break
      }
      setTypePrint(0) // Resetea después de usar
    }
  }, [typePrint, selected])

  return (
    <div className="relative w-full table-fix-head mt-4 h-95   ">
      <table className="table-auto w-full text-gray-600 text-xs text-left border border-gray-300">
        <thead className="select-none font-semibold border-b border-gray-300 bg-gray-50">
          <tr>
            <th className="pl-2 py-2" onClick={(e) => e.stopPropagation()}>
              <FormCheckbox
                id="selectAll"
                name="selectAll"
                label=""
                onChange={onSelectAll}
                checked={selectedAll}
              />
            </th>

            <th
              className="px-2 py-2"
              onClick={() => onChangeSortBy(ShipmentField.SHIPMENT)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Guía</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.SHIPMENT && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-2 py-2"
              onClick={() => onChangeSortBy(ShipmentField.PIECES)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Nº de Piezas</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.PIECES && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-2 py-2 truncate"
              onClick={() => onChangeSortBy(ShipmentField.WEIGHT)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Peso a facturar</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.WEIGHT && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(ShipmentField.ALIAS)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Nombre destinatario</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.ALIAS && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(ShipmentField.PHONE)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Teléfono</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.PHONE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(ShipmentField.CITY)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Ciudad destino</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.CITY && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
            <th className="px-4 py-2">
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Entrega</span>
              </div>
            </th>
            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(ShipmentField.GUIDEALLIED)}
              style={{ maxWidth: '7rem' }}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Nº de Guía aliada</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.GUIDEALLIED && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(ShipmentField.TRACKING)}
              style={{ maxWidth: '7rem' }}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Estatus</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.TRACKING && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-4 py-2"
              onClick={() => onChangeSortBy(ShipmentField.DATE)}
              style={{ maxWidth: '7rem' }}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Fecha de creación</span>
                <div className="h-4 w-4">
                  {sortBy.field == ShipmentField.DATE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th />
          </tr>
        </thead>
        <tbody>
          {shipments.map((shipment, index) => {
            const selected = selectedShipments.some(
              (s) => s.shipmentHeaderID == shipment.shipmentHeaderID
            )

            return (
              <tr
                key={shipment.shipmentHeaderID}
                className={classNames(
                  'hover:bg-gray-200',
                  index % 2 == 0 && 'bg-gray-100',
                  selected && '!bg-blue-200'
                )}
                onClick={async () => {
                  setSelected(shipment)
                  setOpenModal(true)
                  setOpenDetailsModal(true)
                }}
              >
                <th className="pl-2 py-2" onClick={(e) => e.stopPropagation()}>
                  <FormCheckbox
                    id={`select-${shipment.shipmentHeaderID}-${index}`}
                    name={`select-${shipment.shipmentHeaderID}-${index}`}
                    label=""
                    onChange={() => onSelectShipment(shipment)}
                    checked={selected}
                  />
                </th>
                <td className="px-2 py-1 font-semibold">
                  {shipment.shipmentNumber}
                </td>
                <td className="px-2 py-1">{shipment.totalPieces}</td>
                <td className="px-2 py-1">{shipment.totalChargedWeight} Kg</td>
                <td className="px-4 py-1">
                  {shipment.consigneeName?.toUpperCase()}
                </td>
                <td className="px-4 py-1">{shipment.consigneePhone}</td>
                <td className="px-4 py-1">
                  {shipment.locationName?.toUpperCase()}
                </td>
                <td className="px-4 py-1">
                  {shipment.storePickup === '1'
                    ? shipment.buCodeConsignee?.toUpperCase() +
                      ' - ' +
                      shipment.consigneeBUAreaName?.toUpperCase()
                    : 'A DOMICILIO'}
                  <br></br>
                </td>
                <td className="px-4 py-1">
                  {shipment.shipmentReference?.toUpperCase()}
                </td>
                <td
                  className={classNames(
                    'px-4 py-1',
                    shipment.statusID === ShipmentStatus.ANNULLED ||
                      shipment.statusID === ShipmentStatus.REPLACED ||
                      shipment.statusID === ShipmentStatus.DELIVERED
                  )}
                >
                  {shipmentStatusFormat(shipment?.statusID).toUpperCase()}
                </td>
                <td className="px-4 py-1">
                  {moment(shipment.creationDate).format('DD/MM/YYYY')}
                </td>
                <td className="pr-2 " onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    label={TableOptionsButton}
                    hideChevron
                    buttonClassName="!bg-transparent !p-0 !shadow-none hover:bg-gray-100 rounded-full !border-0"
                    menuClassName="!right-full !top-0 !mt-0 !mr-1 w-56 z-50 origin-top-right"
                    items={[
                      {
                        items: [
                          {
                            label: 'Detalles',
                            icon: InformationCircleIcon,
                            onClick: () => {
                              setSelected(shipment)
                              setOpenModal(true)
                              setOpenDetailsModal(true)
                            },
                          },
                          {
                            label: 'Documento Bolsillo',
                            icon: PrinterIcon,
                            onClick: () => {
                              setSelected(shipment)
                              setOpenPrintModal(true)
                            },
                          },
                          {
                            label: 'Etiqueta PDF Pequeña',
                            icon: PrinterIcon,
                            onClick: () => {
                              setSelected(shipment)
                              setOpenPrintPiecesModal(true)
                              setSelectTypePiecesPrint(1)
                            },
                          },
                          {
                            label: 'Etiqueta PDF Grande',
                            icon: PrinterIcon,
                            onClick: () => {
                              setSelected(shipment)
                              setOpenPrintPiecesModal(true)
                              setSelectTypePiecesPrint(2)
                            },
                          },
                          {
                            label: 'Etiqueta Zebra 10x6',
                            icon: PrinterIcon,
                            onClick: () => {
                              setSelected(shipment)
                              setOpenPrintHelpModal(true)
                              setPrintHelpFormat(0)
                            },
                          },
                          {
                            label: 'Etiqueta Zebra 10x10',
                            icon: PrinterIcon,
                            onClick: () => {
                              setSelected(shipment)
                              setOpenPrintHelpModal(true)
                              setPrintHelpFormat(1)
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </td>
              </tr>
            )
          })}

          {shipments.length == 0 && (
            <tr>
              <td colSpan={11} className="px-4 py-4 text-center">
                No hay guías en esta tabla.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {!!selected && !!openDetailsModal && (
        <ShipmentDetailModal
          open={openModal}
          shipmentNumber={selected.shipmentNumber} //selected}
          setOpen={setOpenModal}
          //tracking={tracking}
          onPrint={() => {}}
          onSuccess={onSuccessAction}
          setTypePrint={setTypePrint}
          //loadingTracking={loadingtracking}
        />
      )}

      {!!selected && (
        <ShipmentPrintModal
          open={openPrintModal}
          shipment={selected}
          setOpen={setOpenPrintModal}
          onPrint={() => {}}
        />
      )}

      {!!selected && (
        <ShipmentPiecesPrintModal
          type={selectTypePiecesPrint}
          open={openPrintPiecesModal}
          shipment={selected}
          setOpen={setOpenPrintPiecesModal}
          onPrint={() => {}}
        />
      )}

      <ModalLoading
        open={openLoadingModal}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setOpenLoadingModal}
        onPrint={() => {}}
      />
    </div>
  )
}
